import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { Field, Formik } from "formik";
import React, { useEffect, useMemo, useState } from "react";
import * as Yup from "yup";
import { Alert, Button, Col, Form, Modal, Row } from "react-bootstrap";
import Select from "react-select";
import { LoadingSpinner } from "../../components/common/Loading/LoadingSpinner";
import { taskService } from "../../services";
import { ReactSelectBootstrapStyle } from "../../styles/ReactSelectBootstrapStyle";
import {
    DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE,
    shouldShowFormFieldError,
    showToastError,
    showToastSuccess,
} from "../../utils";

dayjs.extend(utc);

type Props = {
    show: boolean;
    onClose: (added: boolean) => void;
    taskId?: string;
};

type RecurringOption = { label: string; value: string };

type RecurringFormValues = {
    creationDate: string;
    dueDate: string;
    startTime: string;
    endTime: string;
    days: string[];
    recursiveInterval: number | "";
    recursiveDuration: RecurringOption | null;
    commenceDate: string;
};

const recurringTaskDatesSchema = Yup.object({
    creationDate: Yup.string().required(),
    dueDate: Yup.string()
        .required()
        .test(
            "dateOrder",
            "Due date and time must be on or after the start date and time.",
            function (dueDateVal) {
                const p = this.parent as RecurringFormValues;
                const { creationDate, startTime, endTime } = p;
                if (!creationDate || !startTime || !dueDateVal || !endTime) return true;
                const start = dayjs.utc(`${creationDate} ${startTime}`);
                const end = dayjs.utc(`${dueDateVal} ${endTime}`);
                if (!start.isValid() || !end.isValid()) return true;
                return end.isSame(start) || end.isAfter(start);
            }
        ),
    startTime: Yup.string().required("Start time is required."),
    endTime: Yup.string().required("End time is required."),
    days: Yup.array()
        .of(Yup.string())
        .min(1, "Select at least one day.")
        .required(),
    recursiveInterval: Yup.number()
        .transform((value, originalValue) =>
            originalValue === "" || originalValue === null || originalValue === undefined ? NaN : value
        )
        .typeError("Enter a valid interval.")
        .integer("Interval must be a whole number.")
        .min(1, "Interval must be at least 1.")
        .required("Interval is required."),
    recursiveDuration: Yup.mixed<RecurringOption>()
        .nullable()
        .required("Select a duration unit."),
    commenceDate: Yup.string().required("After date is required."),
});

const daysOfWeek = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const ChangeRecurringTaskDates: React.FC<Props> = ({ show, onClose, taskId }) => {
    const [alertVariant, setAlertVariant] = useState("danger");
    const [currentTask, setCurrentTask] = useState<any>();
    const [liveValidationAfterSubmit, setLiveValidationAfterSubmit] = useState(false);

    const validationSchema = useMemo(() => recurringTaskDatesSchema, []);

    useEffect(() => {
        if (taskId) {
            taskService.get(taskId).then((task) => setCurrentTask(task));
        } else {
            setCurrentTask(undefined);
        }
    }, [taskId]);

    const initialValues: RecurringFormValues = useMemo(() => {
        if (!currentTask) {
            const today = dayjs(new Date()).format("YYYY-MM-DD");
            return {
                creationDate: today,
                dueDate: today,
                startTime: "",
                endTime: "",
                days: [],
                recursiveInterval: "",
                recursiveDuration: { label: "Week(s)", value: "Week(s)" },
                commenceDate: today,
            };
        }
        let daysArr: string[] = [];
        if (typeof currentTask.days === "string" && currentTask.days) {
            daysArr = currentTask.days.split(",").filter(Boolean);
        } else if (Array.isArray(currentTask.days)) {
            daysArr = currentTask.days.map((d: unknown) => String(d));
        }
        const durationStr = currentTask.recursiveDuration
            ? String(currentTask.recursiveDuration)
            : "Week(s)";
        return {
            creationDate: dayjs.utc(currentTask.creationDate).format("YYYY-MM-DD"),
            dueDate: dayjs.utc(currentTask.dueDate).format("YYYY-MM-DD"),
            startTime: dayjs.utc(currentTask.creationDate).format("HH:mm"),
            endTime: dayjs.utc(currentTask.dueDate).format("HH:mm"),
            days: daysArr,
            recursiveInterval:
                currentTask.recursiveInterval !== undefined && currentTask.recursiveInterval !== null
                    ? Number(currentTask.recursiveInterval)
                    : "",
            recursiveDuration: { label: durationStr, value: durationStr },
            commenceDate: currentTask.commenceDate
                ? dayjs.utc(currentTask.commenceDate).format("YYYY-MM-DD")
                : dayjs.utc(currentTask.creationDate).format("YYYY-MM-DD"),
        };
    }, [currentTask]);

    const handleClose = () => onClose(false);

    return (
        <Modal centered show={show} keyboard={false} onHide={handleClose}>
            {taskId && !currentTask ? (
                <>
                    <Modal.Header closeButton>
                        <Modal.Title>ReAssign Task</Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="py-5 d-flex justify-content-center">
                        <LoadingSpinner text="Loading task…" />
                    </Modal.Body>
                </>
            ) : (
                <Formik<RecurringFormValues>
                    enableReinitialize
                    key={currentTask?.id ?? "recurring"}
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    validateOnMount={false}
                    validateOnChange
                    validateOnBlur
                    onSubmit={async (values, { setStatus, setSubmitting }) => {
                        if (!currentTask) return;
                        setLiveValidationAfterSubmit(true);
                        setStatus(undefined);
                        setSubmitting(true);
                        try {
                            const response: any = await taskService.edit({
                                ...currentTask,
                                creationDate: dayjs.utc(`${values.creationDate} ${values.startTime}`).toDate(),
                                dueDate: dayjs.utc(`${values.dueDate} ${values.endTime}`).toDate(),
                                days: values.days.join(","),
                                recursiveInterval: values.recursiveInterval,
                                recursiveDuration: values.recursiveDuration
                                    ? values.recursiveDuration.value
                                    : undefined,
                                commenceDate: values.commenceDate,
                            });
                            if (response.status !== "Failure") {
                                setLiveValidationAfterSubmit(false);
                                showToastSuccess("Recurring dates updated successfully!");
                                setTimeout(() => onClose(true), 1000);
                            } else {
                                setAlertVariant("danger");
                                showToastError(DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE);
                            }
                        } catch {
                            setAlertVariant("danger");
                            showToastError(DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE);
                        } finally {
                            setSubmitting(false);
                        }
                    }}
                >
                    {({ errors, status, isSubmitting, handleSubmit, submitCount, values, setFieldValue }) => {
                        const hasPersistedRecord = Boolean(currentTask?.id);
                        const showErr = (field: keyof RecurringFormValues) =>
                            shouldShowFormFieldError(
                                errors,
                                field,
                                hasPersistedRecord,
                                submitCount,
                                liveValidationAfterSubmit
                            );
                        return (
                            <Form noValidate onSubmit={handleSubmit}>
                                <Modal.Header closeButton>
                                    <Modal.Title>ReAssign Task</Modal.Title>
                                </Modal.Header>
                                <Modal.Body>
                                    <Field type="hidden" name="creationDate" />
                                    <Field type="hidden" name="dueDate" />

                                    <Form.Group as={Row} className="mb-3" controlId="startTime">
                                        <Form.Label column sm={3}>
                                            Start Time <span className="text-danger">*</span>
                                        </Form.Label>
                                        <Col sm={3}>
                                            <Field
                                                name="startTime"
                                                type="time"
                                                className={`form-control${showErr("startTime") ? " is-invalid" : ""}`}
                                                aria-invalid={showErr("startTime")}
                                            />
                                            {showErr("startTime") && (
                                                <div className="invalid-feedback d-block">{errors.startTime}</div>
                                            )}
                                        </Col>
                                        <Form.Label column sm={3}>
                                            Due/End Time <span className="text-danger">*</span>
                                        </Form.Label>
                                        <Col sm={3}>
                                            <Field
                                                name="endTime"
                                                type="time"
                                                className={`form-control${showErr("endTime") ? " is-invalid" : ""}`}
                                                aria-invalid={showErr("endTime")}
                                            />
                                            {showErr("endTime") && (
                                                <div className="invalid-feedback d-block">{errors.endTime}</div>
                                            )}
                                        </Col>
                                    </Form.Group>
                                    <Form.Group as={Row} className="mb-3" controlId="days">
                                        <Form.Label column sm={3}>
                                            Day(s) <span className="text-danger">*</span>
                                        </Form.Label>
                                        <Col sm={9} className="pt-2">
                                            {daysOfWeek.map((day, index) => (
                                                <label key={index} className="d-block">
                                                    <Field
                                                        type="checkbox"
                                                        name="days"
                                                        value={day}
                                                        className="form-check-input me-2"
                                                    />
                                                    {day}
                                                </label>
                                            ))}
                                            {showErr("days") && (
                                                <div className="invalid-feedback d-block">{errors.days as string}</div>
                                            )}
                                        </Col>
                                    </Form.Group>
                                    <Form.Group as={Row} className="mb-3" controlId="duration">
                                        <Form.Label column sm={3}>
                                            Every <span className="text-danger">*</span>
                                        </Form.Label>
                                        <Col sm={3}>
                                            <Field
                                                name="recursiveInterval"
                                                type="number"
                                                min={1}
                                                className={`form-control${showErr("recursiveInterval") ? " is-invalid" : ""}`}
                                                aria-invalid={showErr("recursiveInterval")}
                                            />
                                            {showErr("recursiveInterval") && (
                                                <div className="invalid-feedback d-block">{errors.recursiveInterval}</div>
                                            )}
                                        </Col>

                                        <Col sm={6}>
                                            <Select
                                                styles={ReactSelectBootstrapStyle}
                                                classNamePrefix="react-select"
                                                className={showErr("recursiveDuration") ? "is-invalid" : ""}
                                                value={values.recursiveDuration}
                                                onChange={(opt) => setFieldValue("recursiveDuration", opt)}
                                                isClearable={false}
                                                options={[{ label: "Week(s)", value: "Week(s)" }]}
                                            />
                                            {showErr("recursiveDuration") && (
                                                <div className="invalid-feedback d-block">{errors.recursiveDuration as string}</div>
                                            )}
                                        </Col>
                                    </Form.Group>
                                    <Form.Group as={Row} className="mb-3" controlId="commenceDate">
                                        <Form.Label column sm={3}>
                                            After Date <span className="text-danger">*</span>
                                        </Form.Label>
                                        <Col sm={9}>
                                            <Field
                                                name="commenceDate"
                                                type="date"
                                                className={`form-control${showErr("commenceDate") ? " is-invalid" : ""}`}
                                                aria-invalid={showErr("commenceDate")}
                                            />
                                            {showErr("commenceDate") && (
                                                <div className="invalid-feedback d-block">{errors.commenceDate}</div>
                                            )}
                                        </Col>
                                    </Form.Group>

                                    {showErr("dueDate") && errors.dueDate && (
                                        <Alert variant="danger" className="mt-2">
                                            {errors.dueDate}
                                        </Alert>
                                    )}

                                    {status && (
                                        <Alert variant={alertVariant} className="mt-3">
                                            {status}
                                        </Alert>
                                    )}
                                </Modal.Body>
                                <Modal.Footer>
                                    <div className="form-group">
                                        <Button variant="primary" disabled={isSubmitting || !currentTask} type="submit" className="me-2">
                                            {isSubmitting ? (
                                                <LoadingSpinner text="Changing Recurring Dates..." />
                                            ) : (
                                                "Change Recurring Dates"
                                            )}
                                        </Button>
                                        <Button variant="secondary" onClick={handleClose}>
                                            Cancel
                                        </Button>
                                    </div>
                                </Modal.Footer>
                            </Form>
                        );
                    }}
                </Formik>
            )}
        </Modal>
    );
};

export { ChangeRecurringTaskDates };
